/**
 * AISettingsTab - Per-user AI confidence thresholds
 * ==================================================
 * iter1080 - Plain-English rewrite. Removed the internal signal
 * composition table (trade-secret) and swapped every "LLM / signal /
 * multi-criteria" phrase for language a non-accountant, non-technical
 * business owner would use.
 *
 *   1. Review threshold        - "how sure the AI needs to be to auto-do it"
 *   2. Auto-post threshold     - "how sure the AI needs to be to skip Review"
 *   3. Match score threshold   - "how sure it should be that a bank tx = a document"
 *
 * Saved per user; reads/writes /api/user-preferences/ai.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Slider } from '../../components/ui/slider';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Sparkles, Save, RotateCcw, Loader2, Info, CheckCircle2, Target, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../../App';
import { InfoTip } from '../ui/info-tip';

const PLATFORM_MIN_AUTO_POST = 0.80; // iter1207 - Hard floor lowered from 0.85 to 0.80

const AISettingsTab = () => {
  const [reviewT, setReviewT] = useState(0.70);
  const [autoT, setAutoT] = useState(0.80);
  const [matchT, setMatchT] = useState(80);
  const [defaults, setDefaults] = useState({
    review_threshold: 0.70,
    auto_post_threshold: 0.80,
    match_score_threshold: 80,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/user-preferences/ai`);
        setReviewT(res.data?.review_threshold ?? 0.70);
        setAutoT(res.data?.auto_post_threshold ?? 0.80);
        setMatchT(res.data?.match_score_threshold ?? 80);
        setDefaults(res.data?.defaults || defaults);
      } catch (_e) { /* defaults stay */ }
      finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    })();
  }, []);

  const onReviewChange = ([v]) => { setReviewT(v); setDirty(true); };
  const onAutoChange = ([v]) => {
    // iter1207 - Platform floor is 80% - clamp so users can't drag below it.
    const clamped = Math.max(PLATFORM_MIN_AUTO_POST, v);
    setAutoT(clamped);
    setDirty(true);
  };
  const onMatchChange = ([v]) => { setMatchT(v); setDirty(true); };

  const handleSave = async () => {
    if (reviewT > autoT) {
      toast.error('The "hold for me" line must be lower than the "post it for me" line.');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/user-preferences/ai`, {
        review_threshold: reviewT,
        auto_post_threshold: autoT,
        match_score_threshold: matchT,
      });
      toast.success('Saved. Future transactions will use your new settings.');
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save. Please try again.');
    } finally { setSaving(false); }
  };

  const handleReset = () => {
    setReviewT(defaults.review_threshold);
    setAutoT(defaults.auto_post_threshold);
    setMatchT(defaults.match_score_threshold);
    setDirty(true);
  };

  // Human-readable descriptors for the current slider values.
  const reviewDescriptor =
    reviewT >= 0.90 ? 'Very cautious - most items go to Review'
    : reviewT >= 0.75 ? 'Cautious - many items go to Review'
    : reviewT >= 0.60 ? 'Balanced - only fuzzy items go to Review'
    : 'Relaxed - very few items go to Review';

  const autoDescriptor =
    autoT >= 0.95 ? 'Very strict - only near-certain items auto-post'
    : autoT >= 0.90 ? 'Strict - the AI must be very confident'
    : 'Standard - a strong AI hunch is enough';

  const matchDescriptor =
    matchT >= 95 ? 'Only near-perfect matches auto-attach'
    : matchT >= 85 ? 'Strong matches auto-attach (recommended)'
    : matchT >= 70 ? 'Reasonably strong matches auto-attach'
    : 'Loose matches auto-attach (higher risk of wrong pairing)';

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-settings-tab">
      {/* Explainer strip */}
      <Card className="border-fuchsia-200 bg-gradient-to-r from-fuchsia-50 to-pink-50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-fuchsia-600" />
            How much should AkountFly decide on its own?
          </CardTitle>
          <CardDescription className="text-slate-700">
            Higher = more waits for your review. Lower = AkountFly does more on its own.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* 1. Review threshold */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="w-4 h-4 text-amber-600" />
                When should AkountFly hold something for me to check?
              </CardTitle>
              <CardDescription className="mt-1">
                Below this confidence, it waits in <b>Needs Review</b>. <InfoTip title="Review threshold" testId="tip-review-threshold">Move it higher to double-check more; lower if you trust AkountFly to keep going.</InfoTip>
              </CardDescription>
            </div>
            <Badge variant="outline" className="text-sm font-mono">{(reviewT * 100).toFixed(0)}%</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Slider
            value={[reviewT]}
            onValueChange={onReviewChange}
            min={0.50} max={1.00} step={0.01}
            data-testid="review-threshold-slider"
          />
          <div className="flex justify-between text-[11px] text-muted-foreground">
            <span>50%</span><span>Default 70%</span><span>100%</span>
          </div>
          <div className="text-xs text-slate-700 flex items-start gap-2 p-2 rounded bg-slate-50 border">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-500" />
            <span>{reviewDescriptor}.</span>
          </div>
        </CardContent>
      </Card>

      {/* 2. Auto-post threshold */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                When should AkountFly just handle it for me?
              </CardTitle>
              <CardDescription className="mt-1">
                At or above this confidence, it's posted straight away. <InfoTip title="Auto-post threshold" testId="tip-auto-threshold">The minimum is 80%, so only a confident answer ever auto-posts.</InfoTip>
              </CardDescription>
            </div>
            <Badge variant="outline" className="text-sm font-mono">{(autoT * 100).toFixed(0)}%</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Slider
            value={[autoT]}
            onValueChange={onAutoChange}
            min={0.80} max={1.00} step={0.01}
            data-testid="auto-post-threshold-slider"
          />
          <div className="flex justify-between text-[11px] text-muted-foreground">
            <span>Lowest 80%</span><span>Default 80%</span><span>100%</span>
          </div>
          <div className="text-xs text-slate-700 flex items-start gap-2 p-2 rounded bg-slate-50 border">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-500" />
            <span>{autoDescriptor}.</span>
          </div>
        </CardContent>
      </Card>

      {/* 3. Match to existing invoice/bill */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Link2 className="w-4 h-4 text-indigo-600" />
                When should a bank transaction be linked to an invoice or bill?
              </CardTitle>
              <CardDescription className="mt-1">
                How sure a match must be before a bank transaction is paired with an invoice or bill. <InfoTip title="Matching threshold" testId="tip-match-threshold">Every bank movement is checked against your open invoices and bills. Higher means fewer, surer automatic pairings.</InfoTip>
              </CardDescription>
            </div>
            <Badge variant="outline" className="text-sm font-mono">{matchT}%</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Slider
            value={[matchT]}
            onValueChange={onMatchChange}
            min={50} max={100} step={1}
            data-testid="match-score-threshold-slider"
          />
          <div className="flex justify-between text-[11px] text-muted-foreground">
            <span>50%</span><span>Default 80%</span><span>100%</span>
          </div>
          <div className="text-xs text-slate-700 flex items-start gap-2 p-2 rounded bg-slate-50 border">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-500" />
            <span>
              {matchDescriptor}. If you often see bank credits paired with the wrong
              invoice, raise this. If too many payments sit in Review waiting for you
              to match them manually, lower it a little.
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={handleReset} disabled={saving} data-testid="ai-reset-btn">
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Back to defaults
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || !dirty}
          className="bg-[#108981] hover:bg-[#108981]/90"
          data-testid="ai-save-btn"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
          Save
        </Button>
      </div>
    </div>
  );
};

export default AISettingsTab;
